/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import AdminSidebar, { AdminTab } from './components/AdminSidebar';
const DashboardTab = lazy(() => import('./components/DashboardTab'));
// standard tabs will be statically imported where required or lazy loaded if heavy, but for simplicity and to avoid the Vite warning:
// we will just wrap them in lazy, except AdminSidebar which is always rendered.
const SolutionsTab = lazy(() => import('./components/SolutionsTab'));
const ToolsTab = lazy(() => import('./components/ToolsTab'));
const MembersTab = lazy(() => import('./components/MembersTab'));
const UploadTab = lazy(() => import('./components/UploadTab'));
const SettingsTab = lazy(() => import('./components/SettingsTab'));
const AdminsTab = lazy(() => import('./components/AdminsTab'));
const ReviewTab = lazy(() => import('./components/ReviewTab'));
const PageEditorTab = lazy(() => import('./components/PageEditorTab'));
const AuditLogTab = lazy(() => import('./components/AuditLogTab'));
const AdminProfileModal = lazy(() => import('./components/AdminProfileModal'));
const PointsLeaderboardTab = lazy(() => import('./components/PointsLeaderboardTab'));
const AnnouncementTab = lazy(() => import('./components/AnnouncementTab'));
const ContactMessagesTab = lazy(() => import('./components/ContactMessagesTab'));
const DictTab = lazy(() => import('./components/DictTab'));
const MenusTab = lazy(() => import('./components/MenusTab'));
import { getReviewRequestStats } from '@/api/review';
import { getSystemSettings } from '@/api/settings';

const tabLabels: Record<AdminTab, string> = {
  dashboard: '数据概览',
  solutions: '方案管理',
  tools: '工具管理',
  members: '会员管理',
  upload: '文件管理',
  settings: '系统设置',
  admins: '管理员管理',
  review: '操作复核',
  pageeditor: '页面编辑器',
  auditlog: '操作日志',
  points: '灵石排行榜',
  announcement: '站内公告',
  contact: '联系消息',
  dict: '字典管理',
  menus: '菜单管理',
};

const tabIcons: Record<AdminTab, string> = {
  dashboard: 'ri-dashboard-line',
  solutions: 'ri-lightbulb-line',
  tools: 'ri-tools-line',
  members: 'ri-team-line',
  upload: 'ri-folder-line',
  settings: 'ri-settings-3-line',
  admins: 'ri-shield-user-line',
  review: 'ri-shield-check-line',
  pageeditor: 'ri-layout-masonry-line',
  auditlog: 'ri-history-line',
  points: 'ri-sparkling-line',
  announcement: 'ri-megaphone-line',
  contact: 'ri-mail-line',
  dict: 'ri-book-2-line',
  menus: 'ri-menu-add-line',
};

const tabDesc: Record<AdminTab, string> = {
  dashboard: '查看网站整体运营数据',
  solutions: '管理所有开源方案内容',
  tools: '管理所有部署工具资源',
  members: '管理注册会员信息',
  upload: '上传和管理文件资源',
  settings: '配置网站基本参数',
  admins: '管理后台账号与板块权限分配',
  review: '超级管理员审批增删改操作申请',
  pageeditor: '可视化编辑前端页面内容、菜单、Logo 和二级页面',
  auditlog: '记录所有管理员的新增、编辑、删除、发布操作日志',
  points: '查看会员灵石排行、修仙境界，超级管理员可手动赎送灵石',
  announcement: '发布站内公告，会员登录后将看到弹窗提醒',
  contact: '查看联系我们表单提交的消息，支持回复、星标和删除',
  dict: '管理系统数据字典，支持字典类型和字典项的增删改查',
  menus: '可视化配置系统侧边栏栏目菜单、排序及可见状态',
};

import { getDynamicMenus } from '@/api';

// 辅助组件：用于在后台管理面板按需动态加载自定义业务组件
function DynamicComponentLoader({ componentName, readOnly }: { componentName: string; readOnly?: boolean }) {
  const cleanName = componentName.replace(/^\.\/components\//, '').replace(/\.tsx$/, '').trim();
  
  if (!cleanName) {
    return null;
  }

  const LazyComponent = lazy(() => {
    return import(`./components/${cleanName}.tsx`)
      .catch((err) => {
        console.error(`Failed to dynamically load component: ${cleanName}`, err);
        return {
          default: () => (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center max-w-2xl mx-auto my-8 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl animate-bounce"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">找不到前端组件页面</h3>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                系统未在前端目录查找到对应的 React 组件文件：
                <code className="bg-red-50/5 text-red-600 px-1.5 py-0.5 rounded ml-1 font-mono border border-red-100 text-xs">
                  front/src/pages/admin/components/{cleanName}.tsx
                </code>
              </p>
              <div className="bg-gray-50 rounded-xl p-5 text-xs text-gray-400 text-left space-y-2.5 border border-gray-100">
                <p className="font-semibold text-gray-600 flex items-center gap-1">
                  <i className="ri-information-line text-[#0ea5e9]"></i>
                  开发者自助指南：
                </p>
                <ul className="list-disc pl-4 space-y-1.5 text-gray-500 leading-relaxed">
                  <li>请确认您已在 <code className="font-mono text-gray-700 bg-white px-1 py-0.2 border border-gray-150 rounded">front/src/pages/admin/components/</code> 目录下新建了该文件；</li>
                  <li>请确认该文件使用 <code className="font-mono text-gray-700 bg-white px-1 py-0.2 border border-gray-150 rounded">export default</code> 正常导出了您的页签组件；</li>
                  <li>例如：<code className="font-mono text-gray-500">export default function {cleanName}() &#123; return &lt;div&gt;内容&lt;/div&gt;; &#125;</code>；</li>
                  <li>当组件开发完成后，刷新本页面即可正常加载并完美联动！</li>
                </ul>
              </div>
            </div>
          )
        };
      });
  });

  return (
    <Suspense fallback={
      <div className="flex flex-col items-center justify-center p-20 space-y-3 bg-white rounded-2xl border border-gray-100">
        <div className="w-8 h-8 border-4 border-[#0ea5e9] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-xs text-gray-400 font-medium">正在动态按需加载组件...</p>
      </div>
    }>
      <LazyComponent readOnly={readOnly} />
    </Suspense>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const userId = user?.id ?? null;
  const userRole = user?.role ?? null;
  const isSuperAdmin = userRole === 'super_admin';
  const isPromoted = !isSuperAdmin && userRole === 'admin';

  const [allowedTabs, setAllowedTabs] = useState<string[]>([]);
  const [editableTabs, setEditableTabs] = useState<Set<string>>(new Set());
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [dynamicMenus, setDynamicMenus] = useState<any[]>([]);

  // 从 URL Hash 动态获取或校验有效的当前 Tab
  const resolveTabFromHash = (): string => {
    const hash = window.location.hash.replace('#', '');
    if (allowedTabs && allowedTabs.length > 0) {
      return allowedTabs.includes(hash) ? hash : 'dashboard';
    }
    const fallbackTabs = [
      'dashboard','solutions','tools','members','settings',
      'admins','review','pageeditor','auditlog','points','announcement','contact','dict','menus'
    ];
    return fallbackTabs.includes(hash) ? hash : 'dashboard';
  };

  const [activeTab, setActiveTab] = useState<string>(resolveTabFromHash);

  // hash 变化或 allowedTabs 权限加载完时，自动同步/过滤当前的 activeTab 导航状态
  useEffect(() => {
    setActiveTab(resolveTabFromHash());
  }, [location.hash, allowedTabs]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };

  const loadAllowedMenus = useCallback(() => {
    setLoadingMenus(true);
    getDynamicMenus()
      .then((res) => {
        const list = res || [];
        setDynamicMenus(list);
        
        let keys = list.map(m => m.key);
        if (!keys.includes('dashboard')) keys.unshift('dashboard');
        
        // 超级管理员与协作管理员均按当前菜单配置进行授权
        setAllowedTabs(keys);

        const editable = new Set<string>();
        list.forEach(m => {
          if (m.canEdit || isSuperAdmin) editable.add(m.key);
        });
        setEditableTabs(editable);
      })
      .catch((err) => {
        console.error('Failed to load allowed menus:', err);
        setAllowedTabs(['dashboard']);
      })
      .finally(() => {
        setLoadingMenus(false);
      });
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!userId) return;
    loadAllowedMenus();
    window.addEventListener('wanxiang_menus_updated', loadAllowedMenus);
    return () => window.removeEventListener('wanxiang_menus_updated', loadAllowedMenus);
  }, [userId, loadAllowedMenus]);

  useEffect(() => {
    // 若未登录，直接重定向至登录页
    if (!userId) {
      navigate('/login');
      return;
    }
    // 若仅是普通会员（未获得协作管理员授权），则重定向回首页
    if (!isSuperAdmin && !isPromoted) {
      navigate('/');
      return;
    }
    // 当可见菜单数据加载完毕后，校验并确保当前 Tab 在授权列表中
    if (!loadingMenus && allowedTabs.length > 0) {
      if (!allowedTabs.includes(activeTab)) {
        handleTabChange(allowedTabs[0] || 'dashboard');
      }
    }
  }, [userId, isSuperAdmin, isPromoted, navigate, loadingMenus, allowedTabs, activeTab]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAdminProfile, setShowAdminProfile] = useState(false);
  const avatarUrl = user?.avatarUrl || '';

  const handleAdminLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  // 全局无操作自动退出登录监听器
  useEffect(() => {
    if (!userId) return;

    let timeoutMs = 720 * 60 * 1000; // 默认 12 小时
    let idleTimer: any;

    const setupTimeout = async () => {
      try {
        const settingsRes = await getSystemSettings();
        const sessionTimeout = settingsRes?.settings?.sessionTimeout;
        if (sessionTimeout) {
          const timeoutVal = parseInt(sessionTimeout, 10);
          if (!isNaN(timeoutVal) && timeoutVal > 0) {
            timeoutMs = timeoutVal * 60 * 1000;
          }
        }
      } catch (e) {
        console.error('Failed to get sessionTimeout settings:', e);
      }
      resetTimer();
    };

    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        logout();
        alert(`系统安全提示：您已连续较长时间无操作，会话已超时，系统已自动退出登录，请重新登录！`);
        navigate('/login');
      }, timeoutMs);
    };

    // 监听用户活跃事件
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const setupListeners = () => {
      events.forEach((event) => {
        window.addEventListener(event, resetTimer);
      });
    };

    const cleanupListeners = () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };

    // 开启监听与初始计时
    setupListeners();
    setupTimeout();

    return () => {
      clearTimeout(idleTimer);
      cleanupListeners();
    };
  }, [userId, navigate, logout]);

  const [pendingReviews, setPendingReviews] = useState(0);
  const loadPendingReviews = useCallback(async () => {
    if (!userId) return;
    try {
      const scope = isSuperAdmin ? 'all' : 'mine';
      const stats = await getReviewRequestStats(scope);
      setPendingReviews(Number(stats.pending || 0));
    } catch (err) {
      console.error('Failed to load pending review count:', err);
    }
  }, [isSuperAdmin, userId]);

  useEffect(() => {
    void loadPendingReviews();
    const timer = window.setInterval(() => {
      void loadPendingReviews();
    }, 20000);

    return () => {
      window.clearInterval(timer);
    };
  }, [loadPendingReviews]);

  if (userId && (isSuperAdmin || isPromoted) && loadingMenus) {
    return (
      <div className="h-screen bg-[#f8fafc] flex flex-col items-center justify-center gap-3">
        <div className="w-10 h-10 border-4 border-[#0ea5e9] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm text-gray-500 font-medium animate-pulse">正在安全加载菜单与权限...</p>
      </div>
    );
  }

  const currentMenu = dynamicMenus.find(m => m.key === activeTab);
  const activeTitle = currentMenu ? currentMenu.label : (tabLabels[activeTab as AdminTab] || '动态业务');
  const activeIcon = currentMenu ? currentMenu.icon : (tabIcons[activeTab as AdminTab] || 'ri-checkbox-blank-circle-line');
  const activeDescription = currentMenu ? `动态配置业务板块 — ${currentMenu.label}` : (tabDesc[activeTab as AdminTab] || '');

  return (
    <div className="h-screen bg-[#f8fafc] flex overflow-hidden">
      <AdminSidebar activeTab={activeTab as AdminTab} onTabChange={handleTabChange} reviewBadge={pendingReviews} />

      <div className="flex-1 overflow-auto">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-100 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>管理后台</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-700 font-medium">{activeTitle}</span>
            {isPromoted && (
              <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-[#0ea5e9]/10 text-[#0ea5e9] text-xs font-semibold rounded-full border border-[#0ea5e9]/20">
                <i className="ri-shield-user-line text-xs"></i>
                协作管理员
              </span>
            )}
            {isSuperAdmin && (
              <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 text-xs font-semibold rounded-full border border-amber-200">
                <i className="ri-vip-crown-fill text-xs"></i>
                超级管理员
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Review Badge Alert - only super admin */}
            {isSuperAdmin && pendingReviews > 0 && (
              <button
                onClick={() => handleTabChange('review')}
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-xl hover:bg-amber-100 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shield-check-line"></i>
                {pendingReviews} 条待复核
              </button>
            )}

            <div className="h-5 w-px bg-gray-200"></div>

            {/* Admin Avatar with Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); }}
                className="flex items-center gap-2.5 cursor-pointer px-2 py-1.5 rounded-xl hover:bg-gray-50 transition-all"
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="avatar" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold ${
                    isSuperAdmin
                      ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                      : 'bg-gradient-to-br from-[#0ea5e9] to-[#0284c7]'
                  }`}>
                    {(user?.name || 'A')[0].toUpperCase()}
                  </div>
                )}
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-800 leading-none">{user?.name || (isSuperAdmin ? '超级管理员' : '协作管理员')}</p>
                  <p className={`text-xs mt-0.5 font-medium ${ isSuperAdmin ? 'text-amber-500' : 'text-[#0ea5e9]' }`}>
                    {isSuperAdmin ? '超级管理员' : '协作管理员'}
                  </p>
                </div>
                <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`}></i>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-12 w-64 bg-white border border-gray-100 rounded-2xl overflow-hidden z-50">
                  {/* Profile Header */}
                  <div className={`px-4 py-4 border-b border-gray-100 ${
                    isSuperAdmin
                      ? 'bg-gradient-to-br from-amber-50 to-orange-50'
                      : 'bg-gradient-to-br from-[#0ea5e9]/5 to-[#0284c7]/10'
                  }`}>
                    <div className="flex items-center gap-3">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="avatar" className="w-11 h-11 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div className={`w-11 h-11 flex items-center justify-center rounded-xl text-white font-bold text-lg shrink-0 ${
                          isSuperAdmin
                            ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                            : 'bg-gradient-to-br from-[#0ea5e9] to-[#0284c7]'
                        }`}>
                          {(user?.name || 'A')[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{user?.name || '管理员'}</p>
                        <p className="text-xs text-gray-500">{user?.email || (isSuperAdmin ? 'admin@wanxiang.com' : '')}</p>
                        {isSuperAdmin ? (
                          <span className="inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium border border-amber-200">
                            <i className="ri-vip-crown-fill text-amber-500 text-xs"></i>
                            超级管理员
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 bg-[#0ea5e9]/10 text-[#0ea5e9] rounded-full font-medium border border-[#0ea5e9]/20">
                            <i className="ri-shield-user-line text-[#0ea5e9] text-xs"></i>
                            协作管理员
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Permissions summary for promoted admin */}
                    {isPromoted && allowedTabs && allowedTabs.length > 0 && (
                      <div className="mt-3 p-2 bg-white/60 rounded-lg">
                        <p className="text-xs text-gray-400 mb-1">已授权模块</p>
                        <div className="flex flex-wrap gap-1">
                          {allowedTabs.filter(t => t !== 'dashboard').slice(0, 4).map((t) => (
                            <span key={t} className="text-xs px-1.5 py-0.5 bg-[#0ea5e9]/10 text-[#0ea5e9] rounded-md">{tabLabels[t]}</span>
                          ))}
                          {allowedTabs.filter(t => t !== 'dashboard').length > 4 && (
                            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-md">+{allowedTabs.filter(t => t !== 'dashboard').length - 4}</span>
                          )}
                          {allowedTabs.filter(t => t !== 'dashboard').length === 0 && (
                            <span className="text-xs text-gray-400">仅可查看概览</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Menu Items */}
                  <div className="py-1.5">

                    {isSuperAdmin && (
                      <button
                        onClick={() => { handleTabChange('settings'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg">
                          <i className="ri-user-settings-line text-gray-500 text-sm"></i>
                        </div>
                        <span>账号设置</span>
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button
                        onClick={() => { handleTabChange('admins'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg">
                          <i className="ri-shield-user-line text-amber-500 text-sm"></i>
                        </div>
                        <span>管理员管理</span>
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button
                        onClick={() => { handleTabChange('review'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="w-7 h-7 flex items-center justify-center bg-[#0ea5e9]/10 rounded-lg">
                          <i className="ri-shield-check-line text-[#0ea5e9] text-sm"></i>
                        </div>
                        <div className="flex items-center justify-between flex-1">
                          <span>操作复核</span>
                          {pendingReviews > 0 && (
                            <span className="w-5 h-5 flex items-center justify-center bg-amber-400 text-white text-xs rounded-full font-bold">
                              {pendingReviews}
                            </span>
                          )}
                        </div>
                      </button>
                    )}
                    {isPromoted && (
                      <div className="px-4 py-3">
                        <p className="text-xs text-gray-400 text-center">您以协作管理员身份登录</p>
                        <p className="text-xs text-[#0ea5e9] text-center mt-0.5">查看左侧菜单可操作授权功能</p>
                      </div>
                    )}
                  </div>

                  {/* Last Login Info */}
                  <div className="border-t border-gray-100 px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-gray-400">最后登录</span>
                    <span className="text-xs text-gray-500">今天 {new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-100 py-1.5">
                    <button
                      onClick={handleAdminLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-all"
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-red-50 rounded-lg">
                        <i className="ri-logout-box-line text-red-400 text-sm"></i>
                      </div>
                      <span>退出登录</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-8" onClick={() => { showNotifications && setShowNotifications(false); showUserMenu && setShowUserMenu(false); }}>
          {/* Tab Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${activeTab === 'review' ? 'bg-amber-50' : 'bg-[#0ea5e9]/10'}`}>
              <i className={`${activeIcon} text-lg ${activeTab === 'review' ? 'text-amber-500' : 'text-[#0ea5e9]'}`}></i>
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-xl">{activeTitle}</h1>
              <p className="text-xs text-gray-400 mt-0.5">{activeDescription}</p>
            </div>
            {activeTab === 'review' && pendingReviews > 0 && (
              <span className="ml-2 px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                {pendingReviews} 待处理
              </span>
            )}
          </div>

          {(() => {
            const isReadOnly = (tab: AdminTab) => isPromoted && !editableTabs.has(tab);
            return (
              <>
                <Suspense fallback={<div className="p-8 text-center text-gray-400">正在加载组件...</div>}>
                  {activeTab === 'dashboard' && <DashboardTab />}
                  {activeTab === 'solutions' && <SolutionsTab readOnly={isReadOnly('solutions')} />}
                  {activeTab === 'tools' && <ToolsTab readOnly={isReadOnly('tools')} />}
                  {activeTab === 'members' && <MembersTab readOnly={isReadOnly('members')} />}
                  {activeTab === 'upload' && <UploadTab readOnly={isReadOnly('upload')} />}
                  {activeTab === 'settings' && <SettingsTab readOnly={isReadOnly('settings')} />}
                  {activeTab === 'admins' && <AdminsTab isSuperAdmin={isSuperAdmin} />}
                  {activeTab === 'review' && <ReviewTab onUpdated={() => { void loadPendingReviews(); }} />}
                  {activeTab === 'pageeditor' && <PageEditorTab isSuperAdmin={isSuperAdmin} />}
                  {activeTab === 'auditlog' && <AuditLogTab />}
                  {activeTab === 'points' && <PointsLeaderboardTab readOnly={isReadOnly('points')} />}
                  {activeTab === 'announcement' && <AnnouncementTab readOnly={isReadOnly('announcement')} />}
                  {activeTab === 'contact' && <ContactMessagesTab readOnly={isReadOnly('contact')} />}
                  {activeTab === 'dict' && <DictTab readOnly={isReadOnly('dict')} />}
                  {activeTab === 'menus' && <MenusTab readOnly={isReadOnly('menus')} />}
                </Suspense>

                {/* Dynamic component loader for newly inserted dynamic menus */}
                {!['dashboard', 'solutions', 'tools', 'members', 'settings', 'admins', 'review', 'pageeditor', 'auditlog', 'points', 'announcement', 'contact', 'dict', 'menus'].includes(activeTab) && currentMenu?.routePath ? (
                  <DynamicComponentLoader componentName={currentMenu.routePath} readOnly={isReadOnly(activeTab as AdminTab)} />
                ) : !['dashboard', 'solutions', 'tools', 'members', 'settings', 'admins', 'review', 'pageeditor', 'auditlog', 'points', 'announcement', 'contact', 'dict', 'menus'].includes(activeTab) && (
                  <div className="bg-white rounded-2xl border border-gray-150 p-12 text-center flex flex-col items-center justify-center min-h-[500px]">
                    <div className="w-20 h-20 flex items-center justify-center bg-[#0ea5e9]/5 rounded-full mb-6 text-[#0ea5e9] animate-bounce">
                      <i className={`${activeIcon} text-4xl`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-1.5 justify-center">
                      <i className="ri-sparkling-fill text-amber-500 animate-pulse"></i>
                      <span>{activeTitle}板块开发中</span>
                    </h3>
                    <p className="text-sm text-gray-500 max-w-md leading-relaxed mb-6">
                      您好！当前页面 <strong className="text-gray-800 bg-gray-50 px-1.5 py-0.5 rounded">「{activeTitle}」</strong> 是通过数据库动态添加的业务系统入口。
                    </p>
                    <div className="bg-gray-50 rounded-xl p-6 text-xs text-gray-400 text-left space-y-2.5 border border-gray-100 max-w-lg">
                      <p className="font-semibold text-gray-600 flex items-center gap-1">
                        <i className="ri-information-line text-[#0ea5e9]"></i>
                        系统自动化就绪提示：
                      </p>
                      <ul className="list-disc pl-4 space-y-1.5 leading-relaxed text-gray-500">
                        <li>已为您自动完成全套侧边栏渲染及 API 自适应接入。</li>
                        <li>已为您自动打通管理员权限配置勾选矩阵，支持查看/编辑特权分配。</li>
                        <li>只要在菜单的配置中设置对应的“前端组件文件名”（如：<code className="font-mono text-[#0ea5e9] bg-[#0ea5e9]/5 px-1 py-0.2 rounded">MyCustomTab</code>），系统就会自动加载该组件！</li>
                      </ul>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {showAdminProfile && <AdminProfileModal onClose={() => setShowAdminProfile(false)} />}
    </div>
  );
}
